import { useEffect, useState } from 'react';
import { TaskSearchField } from './task-search-field';
import {
  addWireDays,
  formatHours,
  fromWireDate,
  isMonthLocked,
  isoWeek,
  loadOf,
  toWireDate,
  wireDayOfWeek,
  wireMonday,
  withinBackdateWindow,
  type MyWeek,
  type Worklog,
  type WorklogDay,
  type WorklogOverview,
  type WorklogRules,
} from '@forecastify/shared';
import {
  Badge,
  Button,
  DatePicker,
  Icon,
  Progress,
  SKELETON_WIDTHS,
  Segmented,
  Skeleton,
  TextInput,
  Tooltip,
  skeletonWidth,
} from '@forecastify/ui';
import { useCalendarLabels } from '../date-field';
import { useI18n, useT, type Translate } from '../../i18n';
import {
  START_OF_DAY,
  WorklogFields,
  emptyWorklogDraft,
  worklogHours,
  type WorklogDraft,
} from './worklog-form';
import { dayOfMonth, dowLabel } from './week';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The Időrögzítés overview — mockup 2311–2425 and 4082–4137.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Four panels, all reading one week: the **day strip**, the **day's entries** with inline hour
 * editing, the **quick-log** underneath them, and on the right the **week by project** plus
 * **recent entries** to re-log from.
 *
 * ⚠️ **The week/month CHARTS in C11's scope line are not here, and that is not an omission.** They
 * are the *project* overview's (`main.jsx` 2524–2565, `pv.weeks`) — C7's note hands them to C11
 * along with the Idő tab, and they arrive on the project screen where the mockup draws them. This
 * screen's own keys are the four panels above.
 *
 * ⚠️ **`weekCols` and `timesheetRows` exist in the mockup's key bag and are drawn by NOTHING** —
 * `grep` finds zero references in `markup.html`. They are the overview's dead ancestor of C12's
 * timesheet, which has its own live keys (`tsCols`/`tsRows`) in a different section. Not built.
 * CLAUDE.md's *"check the key against `markup.html`"* is exactly this.
 */

export interface TimeScreenProps {
  /** The selected day, `yyyy-mm-dd`. The week is derived from it, server-side. */
  readonly day: string;
  readonly overview: WorklogOverview;
  /** The selected day's entries, in the order the server returned them. */
  readonly entries: readonly Worklog[];
  /** Distinct task-plus-note pairs to re-log, newest first. */
  readonly recents: readonly Worklog[];
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  /**
   * ⚠️ **Item 58 · put one suggestion away.** Optional, so an embedded copy without somewhere to
   * store the dismissal simply draws no control rather than a button that forgets.
   */
  readonly onDismissRecent?: (entry: Worklog) => void;
  /** Search results for the quick-log's task picker. */
  readonly results: readonly TaskChoice[];
  readonly canLog: boolean;
  readonly canEdit: boolean;
  /**
   * C13's rules, and whether the caller may override the backdating half.
   *
   * ⚠️⚠️ **THE SCREEN HAD NO IDEA THESE EXISTED, AND SAID SO OUT LOUD.** Three months back it
   * still rendered *"Nothing logged for this day. Fill it in before the week closes."* over a
   * working quick-log form — an invitation to write into a month `enforceRules` refuses on the
   * server. The rules are pure functions in `packages/shared` precisely so both sides can apply
   * them; the server stays the only security, and this is the UX half.
   *
   * ⚠️ **`mayOverride` is `worklog.lock`**, which lifts the backdating window and NOT the monthly
   * lock — `enforceRules` checks the lock before it consults the permission, so a closed month is
   * closed for everybody.
   */
  readonly rules: WorklogRules;
  readonly mayOverride: boolean;
  readonly serverError: string | null;
  readonly onSelectDay: (day: string) => void;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ 16 Oct 2026 · the reworked homescreen moved the period here.          │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * *Heti* is the day strip; *Havi* is the month as a calendar (`overview.monthDays`). The arrows
   * step one of whichever is on screen, *Ma* comes back, and the picker jumps — a week picker or
   * a month picker, the kit's one `DatePicker` with a `unit`.
   */
  readonly span: 'week' | 'month';
  readonly onSpan: (span: 'week' | 'month') => void;
  readonly onShift: (periods: number) => void;
  readonly onToday: () => void;
  /** A week's Monday or a month's first day, from the picker. */
  readonly onPickPeriod: (start: string) => void;
  /**
   * The caller's allocations for the selected day's week — *A hét összesítése* (16 Oct 2026).
   * `null` until it arrives, or when it could not be read.
   */
  readonly week: MyWeek | null;
  /** Both halves of a line item's address (R-57). */
  readonly onOpenLineItem: (contractIdentifier: string, lineItemIdentifier: string) => void;
  /** An entry's task title opens the task — by its key, which is the task's address (R-57). */
  readonly onOpenTask: (taskKey: string) => void;
  readonly onSearchTasks: (query: string) => void;
  /**
   * ⚠️ Whether the workspace defines any active overtime type — the form's marking control is
   * absent when it does not. The TYPES themselves are the approver's business since `0147`.
   */
  readonly overtimeOffered: boolean;
  readonly onLog: (draft: LogDraft) => void;
  readonly onSetHours: (entry: Worklog, hours: string) => void;
  readonly onDelete: (entry: Worklog) => void;
}

/**
 * ⚠️⚠️ **A STATE, NOT AN ERROR** (`jira.md` §9.3). It sits in the same register as the
 * missing-line-item label: something true about the row, with the reason on hover — never a red
 * failure on somebody's timesheet because an administrator has not connected their Jira yet.
 *
 * ⚠️ **Absent when the hour is not ours to sync at all**, which is most hours in most workspaces.
 * `syncState` is null unless the task carries a remote issue, so a permanent *pending* never
 * appears on work that was never going anywhere.
 *
 * ⚠️ **`synced` draws nothing.** A badge on every successful row is a badge nobody reads, and the
 * information somebody actually wants is the exception.
 */
function SyncBadge({ entry, t }: { readonly entry: Worklog; readonly t: Translate }) {
  if (entry.syncState === null || entry.syncState === 'synced') return null;
  /**
   * ⚠️⚠️ **`detached` MEANS THE HOUR IS STILL YOURS AND ITS COPY IN JIRA IS GONE** (`0084`). Jira
   * cascades an issue deletion to that issue's worklogs, so an hour logged here and pushed
   * successfully gets a tombstone — and it is unlinked rather than deleted, because Jira held a
   * copy and this row is the original. Saying nothing would leave somebody with an hour that
   * silently stopped syncing.
   */
  const key =
    entry.syncState === 'failed'
      ? 'time.sync.failed'
      : entry.syncState === 'detached'
        ? 'time.sync.detached'
        : 'time.sync.pending';
  return (
    <Badge
      variant="outline"
      className="shrink-0"
      /* ⚠️ The provider's own words, untranslated — rule 7 covers the label, not the diagnosis. */
      title={entry.syncError ?? undefined}
    >
      {t(key)}
    </Badge>
  );
}

export interface TaskChoice {
  readonly id: string;
  readonly key: string;
  readonly title: string;
  readonly projectName: string | null;
  readonly projectColor: string | null;
}

/**
 * ⚠️⚠️ **`LogDraft` IS `WorklogDraft` NOW — feedback round 4 item 20.** It used to be this
 * screen's own three fields plus an overtime type, and the task widget and the calendar's dialog
 * each had a different set. `worklog-form.tsx` is the one shape and the one form.
 */
export type LogDraft = WorklogDraft;

/**
 * `2026-08-17` → `2026. aug. 17.` / `17 Aug 2026`.
 *
 * ⚠️ **`toLocaleDateString` with the active locale, not a hand-written month table.** The mockup
 * carries its own `HU_M` array because it has no locale; we have one, and a second month list is a
 * second thing to get wrong. The server builds only the *chart* labels, which are a fixed six.
 */
function dayLabel(date: string, locale: string): string {
  const parsed = fromWireDate(date);
  if (parsed === null) return date;
  return parsed.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * The draft map, minus one entry.
 *
 * ⚠️ **A filtered rebuild rather than `delete`, and the lint rule that forbids the delete is
 * right.** `delete map[id]` on a `Record` mutates the object React is still holding as state — the
 * `{...current}` copy above it makes that safe, but the rule cannot see the copy and the rebuild
 * says what it means anyway: this returns a new map that does not contain the key.
 */
function without(
  map: Readonly<Record<string, string>>,
  key: string,
): Readonly<Record<string, string>> {
  return Object.fromEntries(Object.entries(map).filter(([id]) => id !== key));
}

/** A swatch. Null colour means an unfiled task, and the neutral token says so without words. */
function Swatch({ color, round = false }: { color: string | null; round?: boolean }) {
  return (
    <span
      aria-hidden
      className={round ? 'size-1.5 shrink-0 rounded-full' : 'size-2 shrink-0 rounded-[3px]'}
      style={{ background: color ?? 'var(--muted-foreground)' }}
    />
  );
}

export function TimeScreen({
  day,
  overview,
  entries,
  overtimeOffered,
  recents,
  loading = false,
  onDismissRecent,
  results,
  canLog,
  canEdit,
  rules,
  mayOverride,
  serverError,
  onSelectDay,
  span,
  onSpan,
  onShift,
  onToday,
  onPickPeriod,
  week,
  onOpenLineItem,
  onOpenTask,
  onSearchTasks,
  onLog,
  onSetHours,
  onDelete,
}: TimeScreenProps) {
  const t = useT();
  const { locale } = useI18n();
  const calendar = useCalendarLabels();

  const [pick, setPick] = useState<TaskChoice | null>(null);
  /**
   * ⚠️⚠️ **ONE DRAFT, ONE FORM — feedback round 4 item 20.** This used to be three `useState`s
   * for hours, a note and an overtime type, and no start time at all; the task widget and the
   * calendar's dialog each had their own different set. `worklog-form.tsx` is the form now.
   *
   * ⚠️ Cleared on every submit — an overtime mark that stuck would make the NEXT ordinary hour a
   * claim nobody meant to raise.
   */
  const [draft, setDraft] = useState<WorklogDraft>(() => emptyWorklogDraft(day));

  /* ⚠️ The strip's selected day IS this form's day, so a click on Tuesday re-aims it. The field
     itself is hidden here (`showDate`) because the strip above already says which day it is. */
  useEffect(() => {
    setDraft((current) => ({ ...current, workedOn: day }));
  }, [day]);

  /**
   * ⚠️ **The inline hour editor holds its OWN draft string, keyed by entry.** Deriving the input's
   * value from the parsed model makes intermediate states unrepresentable: typing `8:` would be
   * reformatted or rejected mid-keystroke. The design system records the same rule for
   * `InlineEdit`, and the comma case (`8,0`) is precisely a character the model cannot hold.
   */
  const [drafts, setDrafts] = useState<Readonly<Record<string, string>>>({});

  const dayTotal = entries.reduce((total, entry) => total + Number(entry.hours), 0);

  /**
   * Why this day cannot be written to, if it cannot.
   *
   * ⚠️ **The LOCK is checked first, because it is the one nobody can override** — reporting the
   * backdating window on a closed month would name a rule that is not the one refusing.
   */
  const today = toWireDate(new Date());
  const closed = isMonthLocked(day, today, rules.lockDay)
    ? t('time.closed.month')
    : withinBackdateWindow(day, today, rules, mayOverride).ok
      ? null
      : t('time.closed.backdate', { days: rules.backdateDays });

  const commit = (entry: Worklog): void => {
    const draft = drafts[entry.id];
    setDrafts((current) => without(current, entry.id));
    if (draft === undefined || draft === formatHours(entry.hours)) return;
    onSetHours(entry, draft);
  };

  const submit = (): void => {
    if (pick === null || worklogHours(draft) === null) return;
    onLog({ ...draft, taskId: pick.id });
    setDraft(emptyWorklogDraft(day));
  };

  return (
    <div data-screen="time" className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
      {serverError !== null && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mb-3 rounded-md border px-3 py-2 text-[12.5px]"
        >
          {serverError}
        </p>
      )}

      {/* ── the period: the week as tiles, or the month as a calendar ─────────────────────── */}
      <PeriodCard
        t={t}
        locale={locale}
        calendar={calendar}
        day={day}
        today={today}
        span={span}
        overview={overview}
        loading={loading}
        onSelectDay={onSelectDay}
        onSpan={onSpan}
        onShift={onShift}
        onToday={onToday}
        onPickPeriod={onPickPeriod}
      />

      {/* ⚠️ 16 Oct 2026 · two equal columns, the mockup's `lg:grid-cols-2` — it was 1.2 : 1. */}
      <div className="mt-3 grid grid-cols-[minmax(0,1fr)] items-start gap-3 lg:grid-cols-2">
        {/* ⚠️ The left column: the day's card, and the recents under it. */}
        <div className="flex min-w-0 flex-col gap-4">
          {/* ── the day's entries, and the quick-log ─────────────────────────────────────── */}
          <section
            aria-labelledby="time-day"
            data-tour="time-day"
            className="bg-card text-card-foreground rounded-xl border shadow-sm"
          >
            <div className="flex items-center gap-2 border-b px-4 py-3">
              <span id="time-day" className="text-[13px] font-medium">
                {dayLabel(day, locale)}
              </span>
              <div className="flex-1" />
              <Badge variant="secondary">{formatHours(dayTotal)}</Badge>
            </div>

            {entries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center gap-3 border-b px-4 py-2 last:border-0"
              >
                <Swatch color={entry.projectColor} />
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        onOpenTask(entry.taskKey);
                      }}
                      className="min-w-0 truncate text-left text-[12.5px] font-medium hover:underline"
                    >
                      {entry.taskTitle}
                    </button>
                    <SyncBadge entry={entry} t={t} />
                  </div>
                  <div className="text-muted-foreground truncate text-[11.5px]">
                    {/* ⚠️ Joined with a separator rather than a template with suffixes — Hungarian
                      inflects by vowel harmony and a glued suffix is wrong for half of all names. */}
                    {[entry.taskKey, entry.projectName ?? t('time.noProject'), entry.note]
                      .filter((part) => part !== null && part !== '')
                      .join(' · ')}
                  </div>
                </div>
                <span className="block w-[68px] shrink-0">
                  <TextInput
                    value={drafts[entry.id] ?? formatHours(entry.hours)}
                    onChange={(event) => {
                      const next = event.target.value;
                      setDrafts((current) => ({ ...current, [entry.id]: next }));
                    }}
                    onBlur={() => {
                      commit(entry);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                      if (event.key === 'Escape')
                        setDrafts((current) => without(current, entry.id));
                    }}
                    /* 30 Oct 2026 · a locked month or a day past the window refuses the edit,
                       and the reason is stated below the list. */
                    disabled={!canEdit || closed !== null}
                    aria-label={t('time.entry.hours')}
                    title={t('time.hours.help')}
                    className="h-7 px-2 text-right text-[12.5px] tabular-nums"
                  />
                </span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    onDelete(entry);
                  }}
                  /* 30 Oct 2026 · a delete is refused by the month lock only — the window allows it. */
                  disabled={!canEdit || isMonthLocked(day, today, rules.lockDay)}
                  aria-label={t('time.entry.remove')}
                >
                  <Icon name="trash" size={13} />
                </Button>
              </div>
            ))}

            {entries.length === 0 && (
              <p className="text-muted-foreground border-b px-4 py-8 text-center text-[12.5px] leading-relaxed">
                {closed ?? t('time.day.empty')}
              </p>
            )}

            {/* ⚠️ The reason is stated where the form was, rather than the form being disabled in
              place: a greyed-out box with no explanation reads as a bug. */}
            {canLog && closed !== null && entries.length > 0 && (
              <p className="text-muted-foreground border-t px-4 py-3 text-[12.5px]">{closed}</p>
            )}

            {/* ── quick-log ─────────────────────────────────────────────────────────────── */}
            {canLog && closed === null && (
              <div className="border-t p-3" data-tour="time-quicklog">
                <div className="relative" data-tour="time-task">
                  <div className="text-muted-foreground mb-1 text-[11.5px] font-medium">
                    {t('time.log.task')}
                  </div>

                  {/* 27 Oct 2026 · the shared picker, so the arrow keys work here too. */}
                  <TaskSearchField
                    pick={pick}
                    results={results}
                    onQuery={onSearchTasks}
                    onPick={setPick}
                    onClear={() => {
                      setPick(null);
                    }}
                    swatch={(color) => <Swatch color={color} />}
                  />
                </div>

                {pick !== null && (
                  <div className="mt-2.5">
                    {/* ⚠️⚠️ **THE ONE FORM** — date, start, duration, overtime, description. Item 20;
                      `worklog-form.tsx` argues every field, including why there is no end time and
                      why the date is hidden HERE and shown on the task widget. */}
                    <WorklogFields
                      draft={draft}
                      onChange={setDraft}
                      overtimeOffered={overtimeOffered}
                      showDate={false}
                    />
                    <div className="mt-2 flex">
                      <div className="flex-1" />
                      <Button
                        disabled={worklogHours(draft) === null}
                        onClick={submit}
                        data-tour="time-log"
                      >
                        {t('time.log.submit')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/**
           * ┌───────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ UNDER THE CARD, NOT INSIDE IT — item 31, second pass.            │
           * └───────────────────────────────────────────────────────────────────────┘
           *
           * *"A korábbi bejegyzések kerüljenek át a bal oszlopba, a feladatkereső alá."* The first
           * pass put them in the left column and inside the day's card, which collapsed the two into
           * one block: the recents read as part of the quick-log form rather than as the list they
           * are. *"This is too collapsed together, it shouldn't be part of the card, just the list
           * how it was under the card."*
           *
           * ⚠️ **So the left column is a column now**, holding the card and the list under it — the
           * shape the list had in the right-hand column, moved rather than absorbed.
           *
           * ⚠️ **And it is back to `canLog` alone.** Hiding it on a locked day and while a task was
           * picked were both consequences of living inside the form; outside it there is no room to
           * compete for, and a list that disappears when you pick something is a list you cannot
           * compare against what you picked.
           */}
          {canLog && (
            /* ⚠️ 16 Oct 2026 · its own card again — the reworked mockup draws *Korábbi
               bejegyzések* as a card under the day's, with its hint in the header. */
            <section
              aria-labelledby="time-recents"
              className="bg-card text-card-foreground rounded-xl border shadow-sm"
              data-tour="time-recents"
            >
              <div className="flex items-center gap-2 border-b px-4 py-3">
                <span id="time-recents" className="text-[13px] font-medium">
                  {t('time.recent.title')}
                </span>
                <div className="flex-1" />
                <span className="text-muted-foreground text-[11.5px]">{t('time.recent.hint')}</span>
              </div>
              <div className="py-1">
                {recents.map((entry) => (
                  /**
                   * ⚠️⚠️ **A `group` WRAPPER, NOT A BUTTON INSIDE A BUTTON.** The row IS the action —
                   * it fills the log form — and item 58 adds a second one beside it. Nesting the
                   * dismiss inside the row would be invalid HTML that browsers resolve by hoisting
                   * it out, at which point the click target is anybody's guess.
                   */
                  <div key={entry.id} className="group hover:bg-muted/50 flex items-center">
                    <button
                      type="button"
                      data-tour="time-recent"
                      onClick={() => {
                        setPick({
                          id: entry.taskId,
                          key: entry.taskKey,
                          title: entry.taskTitle,
                          projectName: entry.projectName,
                          projectColor: entry.projectColor,
                        });
                        /* ⚠️ The whole draft, so a re-log carries the START as well as the hours —
                               item 20. The day stays the strip's; the overtime mark deliberately does
                               NOT come along, because re-logging an hour is not re-claiming it. */
                        setDraft({
                          ...emptyWorklogDraft(day),
                          startedAt: entry.startedAt ?? START_OF_DAY,
                          hours: formatHours(entry.hours),
                          note: entry.note ?? '',
                        });
                      }}
                      className="flex min-w-0 flex-1 items-center gap-3 py-2 pl-4 text-left transition-colors"
                    >
                      <Swatch color={entry.projectColor} />
                      <span className="text-muted-foreground w-[64px] shrink-0 truncate text-[11.5px] tabular-nums">
                        {entry.taskKey}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[12.5px]">
                        {entry.taskTitle}
                      </span>
                      <span className="shrink-0 text-[12px] tabular-nums">
                        {formatHours(entry.hours)}
                      </span>
                    </button>

                    {/* ⚠️ **Always in the DOM, revealed on hover and on focus.** A control that only
                            exists on hover is a control a keyboard cannot reach; `opacity` keeps it in
                            the tab order and `focus-visible` brings it back for whoever tabs to it. */}
                    {onDismissRecent === undefined ? null : (
                      <Tooltip content={t('time.recent.dismiss', { task: entry.taskKey })} asChild>
                        <button
                          type="button"
                          aria-label={t('time.recent.dismiss', { task: entry.taskKey })}
                          className="text-muted-foreground hover:text-foreground mr-3 ml-1 shrink-0 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                          onClick={() => {
                            onDismissRecent(entry);
                          }}
                        >
                          <Icon name="x" size={12} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                ))}
                {loading &&
                  SKELETON_WIDTHS.slice(0, 4).map((_widths, index) => (
                    <div
                      key={`skeleton-${String(index)}`}
                      aria-hidden
                      className="flex h-[36px] items-center gap-2.5 px-4"
                    >
                      <Skeleton className="size-2.5 rounded-full" />
                      <Skeleton width="48px" />
                      <div className="min-w-0 flex-1">
                        <Skeleton width={skeletonWidth(index, 1)} />
                      </div>
                      <Skeleton width="32px" />
                    </div>
                  ))}
                {!loading && recents.length === 0 && (
                  <p className="text-muted-foreground px-4 py-3 text-[12px]">
                    {t('time.recent.empty')}
                  </p>
                )}
              </div>
            </section>
          )}
        </div>

        <div className="flex flex-col gap-5">
          <WeekPlan t={t} locale={locale} week={week} onOpenLineItem={onOpenLineItem} />
        </div>
      </div>
    </div>
  );
}

/** `aug 17.` / `Aug 17` — the reader's own short day, which the period labels are made of. */
function shortDay(date: string, locale: string): string {
  const parsed = fromWireDate(date);
  return parsed === null
    ? date
    : parsed.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

/** `2026. augusztus` / `August 2026`. */
function monthName(date: string, locale: string): string {
  const parsed = fromWireDate(date);
  return parsed === null
    ? date
    : parsed.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 16 Oct 2026 · Időlogok — the period, its controls, and its days.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The homescreen mockup's `timeV2`: one card whose header holds *Heti / Havi*, the arrows, *Ma*
 * and a picker, and whose body is the week as seven tiles or the month as a calendar. The hours
 * chart that used to sit on Kezdőlap lives here now, as these two.
 *
 * ⚠️⚠️ **THE TILE IS TINTED WITH ITS VERDICT, WHERE THE OLD STRIP HAD A BAR ON TOP.** Same
 * colours, same kernel — a fill reads at a glance across seven tiles or thirty-one cells, which a
 * 6 px bar did not. The flat verdicts (*pihenőnap*, *még hátra*, *nincs terv*) stay the card's own
 * colour, for the reason `VERDICT_BAR` gives.
 */
function PeriodCard({
  t,
  locale,
  calendar,
  day,
  today,
  span,
  overview,
  loading,
  onSelectDay,
  onSpan,
  onShift,
  onToday,
  onPickPeriod,
}: {
  readonly t: Translate;
  readonly locale: string;
  readonly calendar: ReturnType<typeof useCalendarLabels>;
  readonly day: string;
  readonly today: string;
  readonly span: 'week' | 'month';
  readonly overview: WorklogOverview;
  readonly loading: boolean;
  readonly onSelectDay: (day: string) => void;
  readonly onSpan: (span: 'week' | 'month') => void;
  readonly onShift: (periods: number) => void;
  readonly onToday: () => void;
  readonly onPickPeriod: (start: string) => void;
}) {
  const monday = wireMonday(day) ?? day;
  const sunday = addWireDays(monday, 6) ?? monday;
  const weekLabel = t('time.period.week', {
    from: shortDay(monday, locale),
    to: shortDay(sunday, locale),
    week: isoWeek(monday),
  });
  const current =
    span === 'week' ? wireMonday(today) === monday : today.slice(0, 7) === day.slice(0, 7);

  return (
    <section
      data-tour="time-strip"
      aria-labelledby="time-period"
      className="bg-card text-card-foreground rounded-xl border shadow-sm"
    >
      {/**
       * ⚠️ 21 Oct 2026 · **ON A PHONE, TWO FIXED ROWS** — it wrapped wherever the controls ran out
       * of room, so *Heti / Havi* jumped up beside the title in one view and the next arrow fell to
       * a line of its own in the other. Row one: the title and the span switch. Row two: back, the
       * period itself (the picker, filling the row), forward, *Ma*. The `order` classes move only
       * the phone's layout; the desktop row is as it was.
       */}
      <div
        className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5 phone:px-3"
        data-control-row
      >
        <Icon name="clock" size={15} className="text-muted-foreground shrink-0" />
        <span id="time-period" className="shrink-0 text-[13px] font-medium">
          {t('time.period.title')}
        </span>
        {/* On a phone the picker below says the same, a row lower. */}
        <span className="text-muted-foreground min-w-0 truncate text-[12px] phone:hidden">
          {span === 'week' ? weekLabel : monthName(day, locale)}
        </span>
        <div className="flex-1" />
        <Segmented
          label={t('time.period.span')}
          value={span}
          options={[
            { value: 'week', label: t('time.span.week') },
            { value: 'month', label: t('time.span.month') },
          ]}
          onChange={onSpan}
        />
        <div aria-hidden className="hidden h-0 basis-full phone:order-1 phone:block" />
        <Button
          variant="outline"
          size="icon-sm"
          className="phone:order-2"
          aria-label={t('time.period.previous')}
          onClick={() => {
            onShift(-1);
          }}
        >
          <Icon name="chevron-left" size={15} />
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          className="phone:order-4"
          aria-label={t('time.period.next')}
          onClick={() => {
            onShift(1);
          }}
        >
          <Icon name="chevron-right" size={15} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="phone:order-5"
          disabled={current}
          onClick={onToday}
        >
          {t('time.period.today')}
        </Button>
        <DatePicker
          unit={span}
          value={span === 'week' ? monday : `${day.slice(0, 7)}-01`}
          today={today}
          labels={calendar}
          clearable={false}
          ariaLabel={t('time.period.pick')}
          className="w-auto phone:order-3 phone:min-w-0 phone:flex-1"
          buttonClassName="w-auto phone:w-full"
          formatValue={(value) =>
            span === 'week'
              ? t('time.period.weekPicker', {
                  week: isoWeek(value),
                  from: shortDay(value, locale),
                  to: shortDay(addWireDays(value, 6) ?? value, locale),
                })
              : monthName(value, locale)
          }
          onChange={(value) => {
            if (value !== null) onPickPeriod(value);
          }}
        />
      </div>

      {span === 'week' ? (
        <div className="flex gap-2 p-3">
          {overview.days.map((entry, index) => {
            const tint = tintOf(entry.verdict);
            return (
              <Tooltip key={entry.date} asChild content={dayTitle(t, entry)}>
                <button
                  type="button"
                  onClick={() => {
                    onSelectDay(entry.date);
                  }}
                  /* ⚠️ `aria-pressed`, because the selected day is a STATE and not a link. */
                  aria-pressed={entry.date === day}
                  className={[
                    'relative min-w-0 flex-1 overflow-hidden rounded-lg border px-0 py-2 text-center transition-colors',
                    entry.date === day
                      ? 'border-primary ring-ring/25 ring-[3px]'
                      : 'hover:border-ring/50',
                  ].join(' ')}
                  style={{ background: tint }}
                >
                  <DayOffMark
                    verdict={entry.verdict}
                    className="absolute top-2 right-2 phone:hidden"
                  />
                  <span className="text-muted-foreground block text-[10.5px] uppercase">
                    {dowLabel(t, index)}
                  </span>
                  <span className="block text-[15px] font-medium">{dayOfMonth(entry.date)}</span>
                  {/* ⚠️ An en-dash for a day with NO entries, never `0:00` — "nothing logged" is
                    not "zero hours worked". */}
                  <span
                    className="block text-[12px] font-medium tabular-nums"
                    style={{ color: hourColor(entry.verdict) }}
                  >
                    {entry.hasEntries ? (
                      entry.hours
                    ) : isDayOff(entry.verdict) ? (
                      <>
                        <span className="phone:hidden">–</span>
                        <DayOffMark
                          verdict={entry.verdict}
                          className="hidden justify-center phone:flex"
                        />
                      </>
                    ) : (
                      '–'
                    )}
                  </span>
                </button>
              </Tooltip>
            );
          })}
          {loading && overview.days.length === 0
            ? Array.from({ length: 7 }, (_, index) => (
                <div key={index} aria-hidden className="h-[68px] flex-1 rounded-lg border" />
              ))
            : null}
        </div>
      ) : (
        <MonthGrid
          t={t}
          day={day}
          today={today}
          days={overview.monthDays}
          onSelectDay={onSelectDay}
        />
      )}
    </section>
  );
}

/**
 * *Havi* — the month as a Monday-first calendar, every day carrying its hours in its verdict's
 * colour. The mockup's `tlCells`.
 *
 * ⚠️ **A day with nothing expected shows nothing**, not `0:00`: a blank Saturday is not a
 * shortfall. A working day that has passed with no entries shows `0:00`, because there it is one.
 */
function MonthGrid({
  t,
  day,
  today,
  days,
  onSelectDay,
}: {
  readonly t: Translate;
  readonly day: string;
  readonly today: string;
  readonly days: readonly WorklogDay[] | undefined;
  readonly onSelectDay: (day: string) => void;
}) {
  const first = days?.[0]?.date;
  const lead = first === undefined ? 0 : (wireDayOfWeek(first) ?? 0);
  return (
    <div className="p-3">
      <div className="grid grid-cols-7 gap-1">
        {[0, 1, 2, 3, 4, 5, 6].map((index) => (
          <div
            key={index}
            className="text-muted-foreground pb-1 text-center text-[10.5px] uppercase"
          >
            {dowLabel(t, index)}
          </div>
        ))}
        {Array.from({ length: lead }, (_, index) => (
          <div key={`lead-${String(index)}`} />
        ))}
        {(days ?? []).map((entry) => {
          const flat = tintOf(entry.verdict) === undefined;
          const weekend = (wireDayOfWeek(entry.date) ?? 0) > 4;
          return (
            <Tooltip key={entry.date} asChild content={dayTitle(t, entry)}>
              <button
                type="button"
                aria-pressed={entry.date === day}
                onClick={() => {
                  onSelectDay(entry.date);
                }}
                className={[
                  /* ⚠️ 21 Oct 2026 · tighter on a phone, where *16:45* ran out of a ~44px cell. */
                  'flex h-[44px] min-w-0 flex-col justify-between rounded-md border px-1.5 pt-1 pb-1 text-left transition-colors phone:px-1',
                  entry.date === day
                    ? 'border-primary ring-ring/25 ring-[3px]'
                    : 'hover:border-ring/50',
                ].join(' ')}
                style={{ background: tintOf(entry.verdict) }}
              >
                <span className="flex w-full items-start justify-between gap-1">
                  <span
                    className={`text-[13px] leading-none phone:text-[11.5px] ${
                      entry.date === today
                        ? 'text-foreground font-semibold'
                        : weekend
                          ? 'text-muted-foreground/70'
                          : 'text-muted-foreground'
                    }`}
                  >
                    {dayOfMonth(entry.date)}
                  </span>
                  <DayOffMark verdict={entry.verdict} className="phone:hidden" />
                </span>
                <span
                  className="w-full text-right text-[13px] leading-none font-medium tabular-nums phone:text-[11px] phone:tracking-tight"
                  style={{ color: hourColor(entry.verdict) }}
                >
                  {entry.hasEntries ? (
                    entry.hours
                  ) : isDayOff(entry.verdict) ? (
                    /* 29 Oct 2026 · on a phone the mark sits here, where `0:00` said nothing. */
                    <>
                      <span className="phone:hidden">0:00</span>
                      <DayOffMark
                        verdict={entry.verdict}
                        className="hidden justify-end phone:flex"
                      />
                    </>
                  ) : flat ? (
                    ''
                  ) : (
                    '0:00'
                  )}
                </span>
              </button>
            </Tooltip>
          );
        })}
        {days === undefined
          ? Array.from({ length: 35 }, (_, index) => (
              <div
                key={`wait-${String(index)}`}
                aria-hidden
                className="h-[44px] rounded-md border"
              />
            ))
          : null}
      </div>
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 16 Oct 2026 · *A hét összesítése* — what the week PLANS, line by line.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The reworked mockup's `weekSumVals`: the caller's allocations for the selected week, per line
 * item, against their capacity — where this card used to total the hours LOGGED per project.
 * The product owner confirmed the swap; what was logged is the strip above and the day below.
 *
 * ⚠️ **The bar is the share of CAPACITY**, so four line items filling a week read as four quarter
 * bars; with no capacity at all, a share of the week's plan instead.
 */
function WeekPlan({
  t,
  locale,
  week,
  onOpenLineItem,
}: {
  readonly t: Translate;
  readonly locale: string;
  readonly week: MyWeek | null;
  readonly onOpenLineItem: (contractIdentifier: string, lineItemIdentifier: string) => void;
}) {
  const allocated = Number(week?.allocated ?? '0');
  const capacity = Number(week?.capacity ?? '0');
  const over = loadOf(allocated, capacity) === 'over' && capacity > 0;

  return (
    <section
      aria-labelledby="time-week"
      data-tour="time-week"
      className="bg-card text-card-foreground min-w-0 rounded-xl border shadow-sm"
    >
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <span id="time-week" className="min-w-0 truncate text-[13px] font-medium">
          {week === null
            ? t('time.week.title')
            : t('time.week.summary', {
                from: shortDay(week.from, locale),
                to: shortDay(week.to, locale),
              })}
        </span>
        <div className="flex-1" />
        {week === null ? null : (
          <span
            className="shrink-0 text-[12px] tabular-nums"
            style={{ color: over ? 'var(--status-bad)' : 'var(--muted-foreground)' }}
          >
            {t('time.week.capacity', {
              allocated: formatHours(allocated),
              capacity: formatHours(capacity),
            })}
          </span>
        )}
      </div>
      <div className="p-4">
        {(week?.lineItems ?? []).map((item) => {
          const hours = Number(item.hours);
          const percent =
            capacity > 0
              ? Math.min(100, Math.round((hours / capacity) * 100))
              : allocated > 0
                ? Math.round((hours / allocated) * 100)
                : 0;
          const contract = item.contractIdentifier;
          const lineItem = item.lineItemIdentifier;
          return (
            <button
              key={item.lineItemId}
              type="button"
              disabled={contract === null || lineItem === null}
              onClick={() => {
                if (contract !== null && lineItem !== null) onOpenLineItem(contract, lineItem);
              }}
              className="hover:bg-muted/50 -mx-2 mb-1 block w-[calc(100%+16px)] rounded-md px-2 py-1.5 text-left transition-colors last:mb-0 disabled:cursor-default"
            >
              <span className="flex items-center gap-2 text-[12.5px]">
                <Swatch color={item.projectColor} />
                <span className="min-w-0 flex-1 truncate font-medium">{item.lineItemName}</span>
                <span className="shrink-0 tabular-nums">{formatHours(hours)}</span>
              </span>
              <span className="text-muted-foreground mt-0.5 flex items-center gap-1.5 pl-4 text-[11px]">
                <span className="min-w-0 truncate">
                  {[item.projectName, item.contractCode]
                    .filter((part) => part !== null && part !== '')
                    .join(' · ')}
                </span>
                {item.overtimeTypeName === null ? null : (
                  <span
                    className="shrink-0 rounded px-1 py-px text-[10px] font-medium whitespace-nowrap"
                    style={{
                      background: 'color-mix(in oklch, var(--overtime) 14%, transparent)',
                      color: 'var(--overtime-ink)',
                    }}
                  >
                    {t('time.week.overtime', {
                      name: item.overtimeTypeName,
                      multiplier: Number(item.multiplier).toLocaleString(locale),
                      billed: formatHours(Number(item.billedHours)),
                    })}
                  </span>
                )}
              </span>
              <Progress
                value={percent}
                label={item.lineItemName}
                {...(item.projectColor === null ? {} : { color: item.projectColor })}
                className="mt-1.5"
              />
            </button>
          );
        })}
        {week !== null && week.lineItems.length === 0 ? (
          <p className="text-muted-foreground text-[12px]">{t('time.week.noAllocation')}</p>
        ) : null}
        {week === null
          ? SKELETON_WIDTHS.slice(0, 3).map((_widths, index) => (
              <div key={`skeleton-${String(index)}`} aria-hidden className="mb-3 last:mb-0">
                <Skeleton width={skeletonWidth(index, 0)} />
                <Skeleton className="mt-2 h-1.5" />
              </div>
            ))
          : null}
      </div>
    </section>
  );
}

/**
 * ⚠️ **The mockup's `DAY_KIND` colours, and the three that stay neutral.** `under` / `ok` / `over`
 * are the verdicts a person can act on; `off`, `future` and `free` are states rather than judgements
 * and take the border colour, exactly as `dayStrip` does in the prototype.
 *
 * ⚠️⚠️ **`holiday` IS NOT NEUTRAL — 15 Sep.** It used to arrive as `off` and take the border
 * colour, so a workspace closed on 20 August looked exactly like the Sunday beside it. It is not a
 * judgement either, which is why it is not red: it is the answer to *"why is nothing expected of me
 * today"*, and that answer is worth a colour.
 *
 * ⚠️⚠️ **LEAVE IS AMBER AND A HOLIDAY BLUE — 29 Oct 2026, swapped.** Leave was blue here and amber on
 * the leave dashboard, the logging grid and the resource grid; a person's day off reads the same
 * colour wherever it is drawn now, and the home screen's two cards follow.
 */
const VERDICT_BAR: Readonly<Record<string, string>> = {
  under: 'var(--status-bad)',
  ok: 'var(--status-ok)',
  over: 'var(--log-over)',
  leave: 'var(--leave)',
  holiday: 'var(--status-info)',
};

/**
 * 16 Oct 2026 · a tile's fill: 13 % of its verdict's colour over the card, the mockup's
 * `color-mix(… 13%, var(--card))`. `undefined` for the flat verdicts, which keep the card.
 */
function tintOf(verdict: string | null): string | undefined {
  const color = verdict === null ? undefined : VERDICT_BAR[verdict];
  return color === undefined ? undefined : `color-mix(in srgb, ${color} 13%, var(--card))`;
}

/** The figure follows the bar, except where the bar is neutral — then it stays quiet text. */
function hourColor(verdict: string | null): string {
  const color = verdict === null ? undefined : VERDICT_BAR[verdict];
  return color ?? 'var(--muted-foreground)';
}

/**
 * ⚠️⚠️ **WHICH KIND OF DAY OFF, AT A GLANCE — 27 Oct 2026.** Leave and a public holiday share the
 * `--leave` tint, and a tester asked for a way to tell them apart: a sun for the person's own leave,
 * a flag for the workspace's holiday. The tooltip names which leave type or which holiday.
 *
 * ⚠️ **Off the VERDICT, not the names**: the kernel puts the holiday before the leave and lets
 * logged hours beat both, so a name alone would mark a worked holiday or a swapped Saturday.
 *
 * ⚠️ **29 Oct 2026 · on a phone the mark takes the HOURS line's place**, not a corner: a corner
 * glyph crowded the weekday on a card a seventh of a phone wide. A day off has nothing logged —
 * logged hours turn the verdict into `over` — so the line it replaces only ever said *"–"*.
 */
function isDayOff(verdict: string | null): boolean {
  return verdict === 'leave' || verdict === 'holiday';
}

function DayOffMark({ verdict, className }: { verdict: string | null; className?: string }) {
  const name = verdict === 'leave' ? 'sun' : verdict === 'holiday' ? 'flag' : null;
  if (name === null) return null;
  return (
    <span className={className} style={{ color: VERDICT_BAR[verdict ?? ''] }}>
      <Icon name={name} size={12} />
    </span>
  );
}

/**
 * The tooltip the mockup puts on each pill: the plan, what was logged, and the verdict in words.
 *
 * ⚠️ **The words are the catalogue's, one `t()` per branch with a literal key** — the same seven
 * names the Logolás dashboard shows in its legend, so the two cannot describe a day differently.
 */
function dayTitle(t: Translate, entry: WorklogDay): string {
  const verdict = entry.verdict;
  const note =
    verdict === 'under'
      ? t('dash.verdict.under')
      : verdict === 'ok'
        ? t('dash.verdict.ok')
        : verdict === 'over'
          ? t('dash.verdict.over')
          : verdict === 'off'
            ? t('dash.verdict.off')
            : verdict === 'future'
              ? t('dash.verdict.future')
              : verdict === 'free'
                ? t('dash.verdict.free')
                : verdict === 'leave'
                  ? (entry.leaveName ?? t('dash.verdict.leave'))
                  : verdict === 'holiday'
                    ? entry.holidayName === null
                      ? t('dash.verdict.holiday')
                      : `${t('dash.verdict.holiday')} · ${entry.holidayName}`
                    : '';
  const planned =
    entry.plannedHours === null ? '' : `${t('time.day.plan', { hours: entry.plannedHours })} · `;
  const logged = t('time.day.logged', { hours: entry.hasEntries ? entry.hours : '0:00' });
  return note === '' ? `${planned}${logged}` : `${planned}${logged} · ${note}`;
}
